import React, { useEffect, useState } from "react";
import { supabase } from "../utils/supabaseClient";
import EventEditor from "../components/EventEditor";
import {
  Box,
  Button,
  Typography,
  Paper,
  Avatar,
  Popover,
  Divider,
  Stack,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";

const AdminPage = () => {
  const [user, setUser] = useState<any>(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const toEmail = (u: string) => `${u}@ADMIN.COM`;

  useEffect(() => {
    const getSession = async () => {
      const { data } = await supabase.auth.getSession();
      setUser(data.session?.user ?? null);
      setLoading(false);
    };
    getSession();
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => { listener.subscription.unsubscribe(); };
  }, []);

  const login = async () => {
    setError("");
    const { error } = await supabase.auth.signInWithPassword({
      email: toEmail(username),
      password,
    });
    if (error) setError("Invalid username or password");
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setAnchorEl(null);
  };

  const userInitial = user?.email?.charAt(0).toUpperCase() ?? "A";
  const displayName = user?.email?.split("@")[0] ?? "Admin";
  const popoverOpen = Boolean(anchorEl);

  if (loading) {
    return (
      <Box sx={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#f5f7fb" }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ height: "100vh", display: "flex", justifyContent: "center", alignItems: "center", bgcolor: "#f5f7fb" }}>
        <Paper elevation={6} sx={{ p: 4, width: 360, borderRadius: 4 }}>
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 600, textAlign: "center" }}>
            Admin Login
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, textAlign: "center", color: "text.secondary" }}>
            Enter your credentials to manage events
          </Typography>
          <Stack spacing={2}>
            <Box
              component="input"
              placeholder="Username"
              value={username}
              onChange={(e: any) => setUsername(e.target.value)}
              onKeyDown={(e: any) => e.key === "Enter" && login()}
              sx={{
                width: "100%", p: "12px 14px", borderRadius: 2, fontSize: 16,
                border: "1px solid #e0e0e0", outline: "none", boxSizing: "border-box",
                fontFamily: "inherit",
                "&:focus": { borderColor: "#89C7C8" },
              }}
            />
            <Box
              component="input"
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e: any) => setPassword(e.target.value)}
              onKeyDown={(e: any) => e.key === "Enter" && login()}
              sx={{
                width: "100%", p: "12px 14px", borderRadius: 2, fontSize: 16,
                border: "1px solid #e0e0e0", outline: "none", boxSizing: "border-box",
                fontFamily: "inherit",
                "&:focus": { borderColor: "#89C7C8" },
              }}
            />
            {error && <Typography color="error" fontSize={14}>{error}</Typography>}
            <Button
              variant="contained"
              size="large"
              onClick={login}
              fullWidth
              sx={{ mt: 1, borderRadius: 2, textTransform: "none", fontWeight: 600, bgcolor: "#89C7C8", "&:hover": { bgcolor: "#6fb3b4" } }}
            >
              Sign In
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100vh",
        bgcolor: "#f5f7fb",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      {/* TOP APP BAR */}
      <Box
        sx={{
          height: 56,
          bgcolor: "#fff",
          borderBottom: "1px solid #e8eaf0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: { xs: 2, md: 3 },
          flexShrink: 0,
        }}
      >
        {/* LEFT — branding */}
        <Stack direction="row" spacing={1} alignItems="center">
          <AdminPanelSettingsIcon sx={{ color: "#89C7C8", fontSize: 22 }} />
          <Typography fontWeight={700} fontSize={15} color="text.primary">
            Event Manager
          </Typography>
        </Stack>

        {/* RIGHT — user avatar */}
        <Avatar
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            width: 34,
            height: 34,
            bgcolor: "#9B6DFF",
            fontSize: 14,
            fontWeight: 700,
            cursor: "pointer",
            transition: "opacity 0.2s",
            "&:hover": { opacity: 0.85 },
          }}
        >
          {userInitial}
        </Avatar>
      </Box>

      {/* USER POPOVER */}
      <Popover
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: 3,
            minWidth: 200,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            p: 0,
            overflow: "hidden",
          },
        }}
      >
        {/* USER INFO */}
        <Box sx={{ px: 2.5, py: 2, bgcolor: "#f9f9fb" }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ width: 36, height: 36, bgcolor: "#9B6DFF", fontSize: 14, fontWeight: 700 }}>
              {userInitial}
            </Avatar>
            <Box>
              <Typography fontWeight={700} fontSize={14}>{displayName}</Typography>
              <Typography fontSize={11} color="text.secondary">Administrator</Typography>
            </Box>
          </Stack>
        </Box>

        <Divider />

        {/* LOGOUT */}
        <Box
          onClick={logout}
          sx={{
            px: 2.5,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            cursor: "pointer",
            transition: "background 0.15s",
            "&:hover": { bgcolor: "#fff0f0" },
          }}
        >
          <LogoutIcon sx={{ fontSize: 18, color: "#E05C5C" }} />
          <Typography fontSize={14} fontWeight={600} color="#E05C5C">
            Sign Out
          </Typography>
        </Box>
      </Popover>

      {/* EDITOR CONTAINER */}
      <Paper
        elevation={2}
        sx={{
          m: { xs: 1.5, md: 3 },
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <EventEditor />
      </Paper>
    </Box>
  );
};

export default AdminPage;